import { lazy, Suspense } from 'react';
import { useSearchParams } from 'react-router-dom';

// React Flow + d3-force only load when someone actually opens the canvas.
const GraphExplorer = lazy(() => import('./GraphExplorer'));

// Graph Explorer deep-link boundary — mirrors DocumentDeepLink's `?doc=`
// contract so the canvas is a real navigable location, not ephemeral state:
//
//   ?graph=w:<witness_id>   seed by curated witness (bound fragment set)
//   ?graph=e:<entity_id>    seed by raw entity (expands to its group)
//   ?graph=ev:<event_id>    seed by event (who was there; chronology rows)
//
// Entry buttons NAVIGATE here (history push), so the browser Back button
// closes the canvas and returns to the case — and a graph view is a
// shareable URL. Closing via ✕ / Esc / "Back to case" strips the param with
// replace:true so history doesn't accumulate stale entries. Evidence links
// inside the canvas navigate to `?doc=...` (graph param drops out), which
// unmounts the explorer; Back from the viewer lands on the graph URL again.
export default function GraphDeepLink({ caseId }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get('graph');
  const m = raw ? raw.match(/^(w|e|ev):(.+)$/) : null;
  if (!m) return null;

  const close = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('graph');
    setSearchParams(next, { replace: true });
  };

  return (
    <Suspense fallback={null}>
      <GraphExplorer
        key={raw}
        caseId={caseId}
        witnessId={m[1] === 'w' ? m[2] : undefined}
        entityId={m[1] === 'e' ? m[2] : undefined}
        eventId={m[1] === 'ev' ? m[2] : undefined}
        onClose={close}
      />
    </Suspense>
  );
}
